import axios from 'axios';
import { FormikProvider, useFormik } from 'formik';
import Head from 'next/head';
import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';
import urljoin from 'url-join';

import { InformationCircleIcon } from '@heroicons/react/outline';
import { useResponsive } from '@hooks/useResponsive';
import LoginLayout from '@layouts/v2/login.layout';
import ButtonV2, {
  ButtonV2Rounded,
  ButtonV2Size,
  ButtonV2Style,
} from '@ui-kit/v2/Button';
import {
  ModalV2OneCol,
  bulidCenterOneActionButton,
  useModalV2,
  withModalIconBackground,
} from '@ui-kit/v2/ModalV2';
import { InputTextDecorator } from '@ui-kit/v2/inputs/InputText';
import classNames from 'classnames';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Script from 'next/script';
import config from '../config';
import API_PATHS from '../constants/apiPaths';
import WEB_PATHS from '../constants/webPaths';
import useTranslation from '../i18n/useTranslation';
import { emailSchema } from '../signup/login.schema';
import { getReCaptchaToken } from '../utils/recaptcha';

const apiBaseUrl = process.env.AUTH_API_BASE_URL;

export function useForgotPasswordForm() {
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const emailRef = useRef(null);
  const modal = useModalV2();

  useEffect(() => {
    if (emailRef.current) {
      emailRef.current.scrollIntoView();
      emailRef.current.focus();
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validateOnBlur: false,
    validationSchema: emailSchema,
    onSubmit: handleFormSubmit,
  });

  async function handleFormSubmit(payload: { email: string }) {
    setLoading(true);
    await callApiForgotPassword(payload);
    setLoading(false);
  }

  async function callApiForgotPassword(payload: { email: string }) {
    try {
      const recaptcha = await getReCaptchaToken('forgot_password');
      await axios.post(urljoin(apiBaseUrl, API_PATHS.FORGOT_PASSWORD), {
        ...payload,
        recaptcha,
      });
      modal.toggle(true);
    } catch (error) {
      setErrorMessage(error.message);
    }
  }

  return {
    formik,
    loading,
    emailRef,
    errorMessage,
    handleFormSubmit,
    callApiForgotPassword,
    modal,
  };
}

export default function ForgotPassword() {
  const router = useRouter();
  const { t } = useTranslation();
  const { formik, errorMessage, emailRef, modal } = useForgotPasswordForm();
  const { lg } = useResponsive();

  const ErrorFeedback = () => (
    <div className="mt-4 rounded bg-maroon-100 p-4 text-maroon-600">
      {errorMessage}
    </div>
  );

  const head = useMemo(
    () => (
      <Head>
        <title>
          {t('headerText')} | {t('forgotPasswordPage.metaTitle')}
        </title>
      </Head>
    ),
    [],
  );

  return (
    <LoginLayout head={head} showFooter>
      <div className="rounded-3xl bg-white p-8">
        <Script
          src={`https://www.google.com/recaptcha/api.js?render=${config.RECAPTCHA_SITE_KEY}`}
        ></Script>
        <div className="mb-[30px] flex justify-center">
          <div className="p-3.5">
            <div className="relative h-[54px] w-[54px]">
              <Image
                src="/assets/yournextu-logo-vertical-compact-v2.png"
                layout="fill"
                objectFit="contain"
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col justify-center text-left">
          <div className="mb-[30px]">
            <div className="mb-2 text-subheading font-semibold lg:mb-4 lg:text-h6 lg:font-bold">
              {t('forgotPasswordPage.subtitle')}
            </div>
            <div className="text-p1 font-regular text-grayv2-500 lg:text-p3">
              {t('forgotPasswordPage.caption')}
            </div>
          </div>

          <FormikProvider value={formik}>
            <form
              className="flex w-full flex-col gap-y-6 place-self-start lg:gap-y-8"
              onSubmit={formik.handleSubmit}
              autoComplete="off"
            >
              <div>
                <p className="mb-2 text-left text-p2 font-regular text-grayv2-900">
                  {t('signinPage.email')}
                </p>
                {InputTextDecorator.decorate(
                  {
                    inputProps: {
                      placeholder: t('signinPage.email'),
                      onBlur: formik.handleBlur,
                      onChange: formik.handleChange,
                      className:
                        formik.touched.email && formik.errors.email
                          ? 'text-redv2-900'
                          : '',
                      type: 'email',
                      ref: emailRef,
                    },
                    valid: !(formik.touched.email && formik.errors.email),
                  },
                  [
                    InputTextDecorator.withFormikField('email'),
                    InputTextDecorator.withErrorMessage(
                      <div className="text-left text-footnote text-red-200">
                        <p>{formik.errors.email && t(formik.errors.email)}</p>
                      </div>,
                    ),
                  ],
                )}
              </div>
              {errorMessage?.length > 0 && <ErrorFeedback />}
              <div className="flex flex-col-reverse justify-between gap-y-4 lg:flex-row">
                <div className="text-center text-p1 font-regular text-grayv2-900 lg:text-left">
                  <span>{t('forgotPasswordPage.needhelp')}</span>
                  <span className="ml-2 whitespace-nowrap text-p2 underline">
                    <Link href={WEB_PATHS.HELP_CENTER}>
                      <a target="_blank" rel="noopener noreferrer">
                        {t('extendedFooter.helpCenter')}
                      </a>
                    </Link>
                  </span>
                </div>
                <ButtonV2
                  variantClassName={classNames(
                    ButtonV2Rounded.large,
                    ButtonV2Style.primary,
                    lg ? ButtonV2Size.small : ButtonV2Size.medium,
                    'w-full lg:min-w-1/3 lg:max-w-fit h-fit',
                  )}
                  buttonProps={{
                    disabled: !formik.isValid || !formik.dirty,
                    type: 'submit',
                  }}
                >
                  {t('forgotPasswordPage.getNewPassword')}
                </ButtonV2>
              </div>
            </form>
          </FormikProvider>
        </div>
      </div>
      <ModalV2OneCol
        title={t('forgotPasswordPage.successSubtitle')}
        caption={
          <p className="text-center">
            {t('forgotPasswordPage.successCaption')}
          </p>
        }
        icon={withModalIconBackground(
          <InformationCircleIcon className="h-5 w-5 text-redv2-500" />,
          'bg-redv2-50',
        )}
        {...modal}
        {...bulidCenterOneActionButton({
          label: t('forgotPasswordPage.backToLogin'),
          buttonProps: {
            onClick: () =>
              router.push({ pathname: WEB_PATHS.LOGIN, query: router.query }),
          },
        })}
        closable={false}
      />
    </LoginLayout>
  );
}
